/**
 * Announcement embeds.
 *
 * An announcement used to be a title and a paragraph in a tinted box, which
 * meant "new game added" and "the servers are down in ten minutes" arrived
 * looking identical. An embed gives it structure: a kind that colours it, a
 * heading, optional detail fields, a picture, a footer, and a way out to a
 * link.
 *
 * Every part below the title is optional. One with only a title still renders
 * - it just renders plainly, which is the point: the shape should follow what
 * there is to say rather than forcing every announcement to fill a template.
 */

import type { Announcement, AnnouncementKind } from '@/lib/types';
import { fileUrl } from '@/lib/api';
import { cn } from '@/lib/cn';
import { openExternal } from '@/lib/bridge';
import { Icon } from '@/icons/Icon';
import { EmojiText } from './EmojiText';

/**
 * What each kind looks like.
 *
 * The accent is a CSS variable rather than a hex value so an embed follows
 * the theme and the accent colour like everything else.
 */
const KINDS: Record<AnnouncementKind, { icon: string; accent: string; label: string }> = {
  info: { icon: 'megaphone', accent: 'var(--accent)', label: 'Announcement' },
  success: { icon: 'checkCirc', accent: 'var(--good)', label: 'Now live' },
  warning: { icon: 'shieldNav', accent: 'var(--warn)', label: 'Heads up' },
  danger: { icon: 'ban', accent: 'var(--bad)', label: 'Important' },
  update: { icon: 'sparkle', accent: 'var(--accent-hi)', label: 'Update' },
  event: { icon: 'trophy', accent: 'var(--mythic)', label: 'Event' }
};

export function Embed({ item, compact }: { item: Announcement; compact?: boolean }) {
  const kind = KINDS[item.kind] ?? KINDS.info;
  const fields = item.fields ?? [];

  return (
    <article
      className="relative overflow-hidden rounded-[var(--radius-l)] border border-line-soft bg-surface-1"
      style={{ '--em': kind.accent } as React.CSSProperties}
    >
      {/* The stripe down the left edge. This is the whole colour cue - tinting
          the entire card made long announcements hard to read. */}
      <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-[var(--em)]" />

      {/* A wash behind the header only, so the kind registers at a glance
          without the body text sitting on a coloured field. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[86px]"
        style={{ background: 'linear-gradient(180deg, color-mix(in srgb, var(--em) 11%, transparent), transparent)' }}
      />

      <div className={cn('relative', compact ? 'p-3 pl-4' : 'p-4 pl-5')}>
        {/* who and what */}
        <header className="flex items-start gap-2.5">
          <span
            className="grid h-8 w-8 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))]"
            style={{
              background: 'color-mix(in srgb, var(--em) 18%, transparent)',
              color: 'var(--em)'
            }}
          >
            <Icon name={kind.icon} size={16} />
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span
                className="text-[10px] font-extrabold tracking-[.09em] uppercase"
                style={{ color: 'var(--em)' }}
              >
                {kind.label}
              </span>
              <span className="text-[11px] text-ghost">{when(item.createdAt)}</span>
            </div>
            <h3 className={cn('mt-0.5 leading-snug font-bold', compact ? 'text-[14px]' : 'text-[15.5px]')}>
              <EmojiText text={item.title} />
            </h3>
            {item.subtitle ? (
              <p className="mt-0.5 text-[12.5px] text-mute"><EmojiText text={item.subtitle} /></p>
            ) : null}
          </div>

          {/* A small picture beside the header, for a game icon or a badge. */}
          {item.thumbnail ? (
            <img
              src={fileUrl(item.thumbnail)}
              alt=""
              loading="lazy"
              className="h-[52px] w-[52px] flex-none rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft object-cover"
            />
          ) : null}
        </header>

        {item.body ? (
          <div className="mt-2.5 sm:pl-[42px]">
            <RichText text={item.body} />
          </div>
        ) : null}

        {/* Detail rows. Two across unless one asks for the width. */}
        {fields.length ? (
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 sm:pl-[42px]">
            {fields.map((f, i) => (
              <div key={i} className={cn('min-w-0', !f.inline && 'col-span-2')}>
                <span className="block text-[11px] font-bold tracking-[.05em] text-dim uppercase">
                  {f.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] leading-relaxed break-words text-mute">
                  {f.value}
                </span>
              </div>
            ))}
          </div>
        ) : null}

        {item.image ? (
          <div className="mt-3 sm:pl-[42px]">
            <img
              src={fileUrl(item.image)}
              alt=""
              loading="lazy"
              className="max-h-[340px] w-full rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft object-cover"
            />
          </div>
        ) : null}

        {(item.footer || item.link) ? (
          <footer className="mt-3 flex flex-wrap items-center gap-3 sm:pl-[42px]">
            {item.link ? (
              <button
                type="button"
                onClick={() => openExternal(item.link!)}
                className="flex h-[30px] items-center gap-1.5 rounded-[calc(10px*var(--radius-mul,1))] px-3 text-[12.5px] font-bold transition-[background-color] hover:bg-[color-mix(in_srgb,var(--em)_32%,transparent)]"
                style={{ background: 'color-mix(in srgb, var(--em) 20%, transparent)', color: 'var(--em)' }}
              >
                {item.linkLabel || 'Open'}
                <Icon name="chevronR" size={13} />
              </button>
            ) : null}
            {item.footer ? (
              <span className="text-[11.5px] text-ghost">{item.footer}</span>
            ) : null}
          </footer>
        ) : null}
      </div>
    </article>
  );
}

/**
 * The body, with the little bit of markup people actually use.
 *
 * Not a markdown parser - **bold**, `code`, bullet lines and blank-line
 * paragraphs, which covers what an announcement is. Anything else stays as
 * the characters it is, so nothing can be smuggled in as markup.
 */
function RichText({ text }: { text: string }) {
  const blocks = text.split(/\n{2,}/);

  return (
    <div className="flex flex-col gap-2">
      {blocks.map((block, bi) => {
        const lines = block.split('\n');
        const bullets = lines.every((l) => /^\s*[-*•]\s+/.test(l));

        if (bullets) {
          return (
            <ul key={bi} className="flex flex-col gap-1">
              {lines.map((l, li) => (
                <li key={li} className="flex gap-2 text-[13px] leading-relaxed text-dim">
                  <span className="mt-[7px] h-1 w-1 flex-none rounded-full bg-[var(--em)]" />
                  <span className="min-w-0">{inline(l.replace(/^\s*[-*•]\s+/, ''))}</span>
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={bi} className="text-[13px] leading-relaxed text-dim">
            {lines.map((l, li) => (
              <span key={li}>
                {li ? <br /> : null}
                {inline(l)}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

/**
 * **bold**, `code` and the server's own emoji, and nothing else.
 *
 * Plain runs go through EmojiText rather than being pushed as raw strings, so
 * an announcement can use the same :hype: everybody else can. Code spans do
 * not - inside backticks the characters are the point.
 */
function inline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|`(.+?)`/g;
  let at = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(text))) {
    if (m.index > at) out.push(<EmojiText key={'t' + at} text={text.slice(at, m.index)} />);
    if (m[1] !== undefined) {
      out.push(
        <strong key={m.index} className="font-bold text-ink">
          <EmojiText text={m[1]} />
        </strong>
      );
    } else {
      out.push(
        <code key={m.index} className="rounded bg-surface-3 px-1 py-px font-mono text-[12px] text-ink">
          {m[2]}
        </code>
      );
    }
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push(<EmojiText key={'t' + at} text={text.slice(at)} />);
  return out;
}

function when(at: number): string {
  const d = new Date(at);
  const days = Math.floor((Date.now() - at) / 86_400_000);
  if (days === 0) return 'Today at ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return 'Yesterday';
  if (days < 7) return days + ' days ago';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

export { KINDS as EMBED_KINDS };
